import { Component, input, output } from '@angular/core';

/** Um passo do formulário: a chave que o identifica e o que se lê no cabeçalho. */
export interface FormStep {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
}

/** Em que ponto está cada passo — comanda a marca no círculo e a cor. */
export type FormStepState = 'vazio' | 'parcial' | 'completo' | 'erro';

/**
 * Barra de passos de um formulário longo.
 *
 * <p>Os passos não se percorrem por ordem obrigatória: quem preenche sabe o que tem à mão e o
 * que fica para depois. O que a barra faz é dizer, de relance, o que já está preenchido — e
 * onde ficou o campo que impede de gravar.
 *
 * <p>Um passo pode, ainda assim, estar trancado: continua à vista, a cinza-claro e sem resposta
 * ao clique, para se perceber que o formulário continua para lá do ponto em que se está. Quem
 * tranca diz porquê em {@link lockedReason}.
 */
@Component({
  selector: 'app-form-steps',
  template: `
    <div class="fs-steps" role="tablist">
      @for (step of steps(); track step.key; let last = $last) {
        <button
          type="button"
          role="tab"
          class="fs-step"
          [class.fs-step--current]="step.key === current()"
          [class.fs-step--done]="stateOf(step.key) === 'completo'"
          [class.fs-step--partial]="stateOf(step.key) === 'parcial'"
          [class.fs-step--error]="stateOf(step.key) === 'erro'"
          [class.fs-step--locked]="isLocked(step.key)"
          [attr.aria-selected]="step.key === current()"
          [attr.aria-disabled]="isLocked(step.key)"
          [disabled]="isLocked(step.key)"
          [title]="isLocked(step.key) ? lockedReason() : null"
          (click)="select.emit(step.key)"
        >
          <span class="fs-step-bullet">
            @if (stateOf(step.key) === 'erro') {
              <i class="fe fe-alert-circle"></i>
            } @else if (stateOf(step.key) === 'completo') {
              <i class="fe fe-check"></i>
            } @else {
              <i class="fe {{ step.icon }}"></i>
            }
          </span>
          <span class="fs-step-label">{{ step.label }}</span>
        </button>
        @if (!last) { <span class="fs-step-line"></span> }
      }
    </div>
  `,
})
export class FormSteps {
  readonly steps = input.required<readonly FormStep[]>();
  readonly current = input.required<string>();
  /** Estado por chave; o que não vier na lista conta como vazio. */
  readonly states = input<Record<string, FormStepState>>({});
  /** Chaves dos passos trancados — à vista, mas sem resposta ao clique. */
  readonly locked = input<readonly string[]>([]);
  /** O que se lê ao passar por cima de um passo trancado. */
  readonly lockedReason = input('Ainda não disponível neste ponto.');

  readonly select = output<string>();

  protected stateOf(key: string): FormStepState {
    return this.states()[key] ?? 'vazio';
  }

  protected isLocked(key: string): boolean {
    return this.locked().includes(key);
  }
}
